<template>
  <div class="h-full w-full flex flex-col justify-between overflow-y-auto text-app-color px-20 md:px-0">
    <div class="w-full h-full md:pl-100 md:px-20 pt-35 md:pt-67 pb-70">
      <div class="flex items-center md:hidden" @click="back">
        <i class="text-12 mr-10 icon-keyboard-arrow-left opacity-50" />
        <p class="text-opacity-80">Back</p>
      </div>

      <div class="md:flex items-center justify-between md:w-750 md:max-w-750">
        <h2 class="font-bold text-35 md:text-21 mr-10">Your team</h2>
        <div v-if="!isRegularUser" class="flex items-center flex-grow md:justify-end">
          <InputModule
            v-model="email"
            name="email"
            type="email"
            placeholder="email address"
            class="max-w-185 w-full mr-10"
            shadow="false"
            :has-background="false"
            :input-classes="`text-app-color focus:outline-none py-11 leading-normal rounded-6
                   placeholder-app-color opacity-50 focus:opacity-100 anim-opacity text-13
                   ${isDarkMode ? 'bg-transparent border-b-2 border-dark-900 rounded-none' : 'bg-grey-850 pl-10'}`"
            :error-message="errorMessage"
            @clearErrorMessage="clearErrorMessage"
            @keydown.enter="invite"
          />

          <ButtonModule
            text="add user"
            class="text-13 rounded-6 py-11 px-10 text-white font-medium bg-link-color"
            :class="isDraculaTheme ?
              'bg-notebooks-dracula-link hover:bg-canvas border-2 border-notebooks-dracula-link'
              : isDarkMode ?
                'bg-link-color hover:bg-canvas hover:text-link-color border-2 border-link-color'
                : 'bg-link-color hover:bg-canvas hover:text-link-color border-2 border-link-color'"
            prefix="icon-add text-10 mt-2 mr-6"
            :icon-has-margin="false"
            button-opacity="100"
            @click="invite"
          />
        </div>
      </div>

      <div class="mt-30">
        <Table
          component="Member"
          :total-members="totalMembers"
          no-members-message="Members not found"
          :members="members"
          @getMembers="getMembers"
        />
      </div>

      <div v-if="temporaryMembers && temporaryMembers.length > 0" class="mt-30 pb-50">
        <h2 class="font-bold text-35 md:text-21 mr-10">Invitations pending</h2>

        <Table
          class="mt-20"
          component="TemporaryMember"
          :members="temporaryMembers"
          :hide-header="true"
          :hide-footer="true"
          no-members-message="No invitations pending"
          :total-members="totalTemporaryMembers"
          @getMembers="getTempMembers"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, computed, ref } from 'vue'
import InputModule from '@/components/shared/form-controls/InputModule'

import ButtonModule from '@/components/shared/form-controls/ButtonModule'
import Table from '@/components/team/Table'
import { useStore } from 'vuex'
import { teamsService } from '@/services'
import { emailValidation } from '@/helpers'

export default defineComponent({
  components: {
    ButtonModule,
    InputModule,
    Table
  },

  emits: ['back'],

  setup (_, { emit }) {
    const { state, getters, dispatch, commit } = useStore()

    dispatch('getMembers')
    dispatch('getTemporaryMembers')

    const isDarkMode = computed(() => getters.isDarkMode)
    const isDraculaTheme = computed(() => state.settings.settings.theme === 'Dracula')

    const members = computed(() => state.team.members)
    const totalMembers = computed(() => state.team.totalMembers)
    const temporaryMembers = computed(() => state.team.temporaryMembers)
    const totalTemporaryMembers = computed(() => state.team.totalTemporaryMembers)
    const isRegularUser = computed(() => getters.isRegularUser)

    const email = ref('')
    const errorMessage = ref(null)

    function back () {
      emit('back')
    }

    function invite () {
      errorMessage.value = emailValidation(email.value)

      if (!errorMessage.value) {
        teamsService.inviteMembers({ emails: [email.value] }).then(() => {
          email.value = ''
          commit('setNotification', {
            isVisible: true,
            message: 'Invite successfully sent'
          })
          dispatch('getTemporaryMembers')
        })
      }
    }

    function getMembers (value) {
      dispatch('getMembers', value)
      dispatch('getTemporaryMembers', {
        search: value.search
      })
    }

    function getTempMembers (value) {
      dispatch('getTemporaryMembers', value)
    }

    function clearErrorMessage () {
      errorMessage.value = null
    }

    return {
      email,
      errorMessage,
      members,
      totalMembers,
      temporaryMembers,
      totalTemporaryMembers,
      isDarkMode,
      isDraculaTheme,
      isRegularUser,

      back,
      invite,
      clearErrorMessage,
      getMembers,
      getTempMembers
    }
  }

})
</script>
